import { useState, useContext } from "react";
import PropTypes from "prop-types";
import { AppContext } from "../../../pages/root/Root";
import { CreativeReviewContext } from "../../../contexts/contexts";
import Grid from "@mui/material/Unstable_Grid2";
import { Button, TextField, FormControlLabel, Checkbox } from "@mui/material";
import {
  AttachFileOutlined,
  BlockOutlined,
  SendOutlined,
} from "@mui/icons-material";

import { AppAlert } from "../appAlert/AppAlert";
import { CreativeClassification as ListView } from "../creativeClassification/CreativeClassification";
import { PoolMedia } from "../poolMedia/PoolMedia";
import { ProductListing as PathmaticsProductListing } from "../../pathmatics/productListing/ProductListing";
import { ProductListing } from "../productListing/ProductListing";

import { updateCreativeStrings } from "../updateCreative/locale";
import { productListingStrings } from "../productListing/locale";

import {
  ACTIVATION_FILTER_OPTIONS,
  MAPPING_KEYS_STATUSES,
  MAPPING_OPTIONS,
  DATA_SOURCE,
} from "../../../constants";

import { initLocalization } from "../../../utils/helpers";

import "../../../style/common.scss";

export const UpdateCreativeList = ({
  dataSource,
  creativeDetailsData,
  showProductListing = true,
  onClose,
}) => {
  useContext(AppContext);
  initLocalization(updateCreativeStrings);
  const { metadataComponent, useValidVisual, useMappingKeyStatus } = useContext(
    CreativeReviewContext,
  );

  const [error, setError] = useState(null);
  const [selectedCreatives, setSelectedCreatives] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState({});
  const [creatives, setCreatives] = useState(creativeDetailsData || []);
  const [paperClipLabel, setPaperClipLabel] = useState("");
  const [resetSelectedCreatives, setResetSelectedCreatives] = useState(true);
  const [changeAll, setChangeAll] = useState(false);

  const {
    isLoading: actionLoading,
    paperClipMappingKey,
    rejectMappingKey,
    validateMappingKey,
  } = useMappingKeyStatus(
    MAPPING_OPTIONS[`${dataSource?.toLowerCase()}Creative`],
  );

  const { isLoading: validVisualLoading, setValidVisual } = useValidVisual();

  let creative = {};

  if (selectedCreatives?.length === 1) {
    creative = {
      id: selectedCreatives[0]?.id,
      productId: selectedCreatives[0]?.product?.sourceProductId,
      isValidVisual: selectedCreatives[0]?.isValidVisual,
    };
  }

  const disablePaperClipButton = () => {
    return !selectedCreatives?.length || !paperClipLabel || actionLoading;
  };

  const disableRejectButton = () => {
    return !selectedCreatives?.length || actionLoading;
  };

  const disableValidateButton = () => {
    return !selectedProduct.id || !selectedCreatives?.length || actionLoading;
  };

  const buildPayload = (selectedCreatives, selectedProductId) => {
    const payload = {
      lineIds: !changeAll
        ? selectedCreatives.map((row) => row.id)
        : creatives.map((c) => c.id),
    };

    if (selectedProductId) payload.productId = selectedProductId;

    return payload;
  };

  const updateCreatives = (lineIds, remove = false) => {
    const newCreatives = remove
      ? [...creatives].filter((creative) => !lineIds.includes(creative.id))
      : [...creatives];

    if (!newCreatives.length || changeAll) {
      onClose();
      return;
    }

    const creativeIndex = creatives.findIndex(
      (item) => item.id === selectedCreatives[selectedCreatives.length - 1]?.id,
    );

    let nextCreative = creatives[creativeIndex + 1];

    if (!nextCreative) {
      nextCreative = creatives[0];
    }

    setCreatives(newCreatives);
    setSelectedCreatives([nextCreative]);
  };

  const handlePaperClipFieldChange = (e) => {
    setPaperClipLabel(e.target.value);
  };

  const handlePaperClipMappingKeys = () => {
    const data = {
      ...buildPayload(selectedCreatives),
      paperclipLabel: paperClipLabel,
    };
    paperClipMappingKey(data)
      .then(() => updateCreatives(data.lineIds, true))
      .catch((err) => setError(err));
  };

  const handleRejectMappingKeys = () => {
    const data = buildPayload(selectedCreatives);
    rejectMappingKey(data)
      .then(() =>
        updateCreatives(
          data.lineIds,
          selectedCreatives[0]?.status !== MAPPING_KEYS_STATUSES.rejected,
        ),
      )
      .catch((err) => setError(err));
  };

  const handleValidateMappingKeys = () => {
    const data = buildPayload(selectedCreatives, selectedProduct.id);
    validateMappingKey(data)
      .then(() =>
        updateCreatives(
          data.lineIds,
          selectedCreatives[0]?.product?.id !== selectedProduct.id,
        ),
      )
      .catch((err) => setError(err));
  };

  const handleSetValidVisual = async () => {
    const isValidVisual = !creative.isValidVisual;
    await setValidVisual({
      id: creative.id,
      isValidVisual,
    });
    setSelectedCreatives((prev) => {
      const next = [...prev];
      next[0].isValidVisual = isValidVisual;
      return next;
    });
    setCreatives((prev) => {
      const next = [...prev];
      next.find((item) => item.id === selectedCreatives[0].id).isValidVisual =
        isValidVisual;
      return next;
    });
  };

  const handleCloseError = () => setError(false);

  return (
    <>
      <AppAlert
        key={error}
        open={Boolean(error)}
        type="error"
        onClose={handleCloseError}
      >
        {productListingStrings.errors[error]}
      </AppAlert>

      <Grid
        className="update-creative-list"
        container
        rowSpacing={{ xs: 3, md: 0 }}
        columnSpacing={{ xs: 0, md: 2 }}
        py={6}
        px={{ xs: 2, md: 6 }}
        height="100%"
        disableEqualOverflow
      >
        <Grid
          md={
            showProductListing &&
            creatives[0].status !==
              MAPPING_KEYS_STATUSES.waitingProductRequested
              ? 6
              : 12
          }
          xs={12}
          position="relative"
          height="100%"
        >
          <ListView
            poolMediaComponent={(props) => <PoolMedia {...props} />}
            metadataComponent={metadataComponent}
            poolData={creatives}
            selectedCreatives={selectedCreatives}
            resetSelectedCreatives={resetSelectedCreatives}
            onCreativesSelection={setSelectedCreatives}
            onResetSelectedCreatives={setResetSelectedCreatives}
          />
        </Grid>
        {showProductListing &&
          creatives[0].status !==
            MAPPING_KEYS_STATUSES.waitingProductRequested && (
            <Grid md={6} xs={12}>
              <Grid container direction="column" columns={12} height="100%">
                <Grid xs={12} flexGrow="1" sx={{ position: "relative" }}>
                  <FormControlLabel
                    className="update-creative__change-all"
                    control={<Checkbox checked={changeAll} />}
                    label={updateCreativeStrings.changeAll}
                    onChange={() => setChangeAll(!changeAll)}
                  />
                  {dataSource === DATA_SOURCE.pathmatics ? (
                    <PathmaticsProductListing
                      activationFilterOption={
                        ACTIVATION_FILTER_OPTIONS.creatives
                      }
                      mappingKey={creative}
                      allowSearch={true}
                      onSelectedProductRow={setSelectedProduct}
                      onSetValidVisual={handleSetValidVisual}
                      shouldRefresh={1}
                      isLoading={actionLoading || validVisualLoading}
                    />
                  ) : (
                    <ProductListing
                      activationFilterOption={
                        ACTIVATION_FILTER_OPTIONS.creatives
                      }
                      mappingKey={creative}
                      allowSearch={true}
                      onSelectedProductRow={setSelectedProduct}
                      onSetValidVisual={handleSetValidVisual}
                      shouldRefresh={1}
                      isLoading={actionLoading || validVisualLoading}
                    />
                  )}
                </Grid>
                <Grid
                  display="flex"
                  flexDirection={{ xs: "column", sm: "row" }}
                  gap={2}
                  paddingY={3}
                  justifyContent="space-between"
                >
                  <Grid
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    <TextField
                      id="outlined-disabled"
                      label={updateCreativeStrings.paperClipLabel}
                      size="small"
                      value={paperClipLabel}
                      inputProps={{ maxLength: 255 }}
                      onChange={handlePaperClipFieldChange}
                    />
                    <Button
                      startIcon={<AttachFileOutlined />}
                      variant="outlined"
                      size="large"
                      disabled={disablePaperClipButton()}
                      onClick={handlePaperClipMappingKeys}
                    >
                      {updateCreativeStrings.paperClip}
                    </Button>
                  </Grid>
                  <Grid
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    <Button
                      startIcon={<BlockOutlined />}
                      variant="outlined"
                      color="error"
                      size="large"
                      disabled={disableRejectButton()}
                      onClick={handleRejectMappingKeys}
                    >
                      {updateCreativeStrings.reject}
                    </Button>
                    <Button
                      startIcon={<SendOutlined />}
                      variant="outlined"
                      color="success"
                      size="large"
                      disabled={disableValidateButton()}
                      onClick={handleValidateMappingKeys}
                    >
                      {updateCreativeStrings.validate}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
      </Grid>
    </>
  );
};

UpdateCreativeList.propTypes = {
  dataSource: PropTypes.string.isRequired,
  creativeDetailsData: PropTypes.arrayOf(PropTypes.objectOf).isRequired,
  showProductListing: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
};
